import { lockBodyScroll, unlockBodyScroll } from "@/utils/bodyScrollLock";

interface OverlayEffectsState {
  keyDown: boolean;
  documentMouseDown?: boolean;
  scrollLock: boolean;
}

export class OverlayEffects {
  private isKeyDownAttached = false;
  private isDocumentMouseDownAttached = false;
  private isScrollLocked = false;

  constructor(
    private readonly onKeyDown: (event: KeyboardEvent) => void,
    private readonly onDocumentMouseDown?: (event: MouseEvent) => void,
  ) {}

  sync(state: OverlayEffectsState): void {
    this.syncKeyDown(state.keyDown);
    this.syncDocumentMouseDown(Boolean(state.documentMouseDown));
    this.syncScrollLock(state.scrollLock);
  }

  reset(): void {
    this.detachKeyDown();
    this.detachDocumentMouseDown();
    this.unlockScroll();
  }

  private syncKeyDown(enabled: boolean): void {
    if (enabled) {
      this.attachKeyDown();
      return;
    }

    this.detachKeyDown();
  }

  private syncDocumentMouseDown(enabled: boolean): void {
    if (enabled) {
      this.attachDocumentMouseDown();
      return;
    }

    this.detachDocumentMouseDown();
  }

  private syncScrollLock(enabled: boolean): void {
    if (enabled) {
      this.lockScroll();
      return;
    }

    this.unlockScroll();
  }

  private attachKeyDown(): void {
    if (this.isKeyDownAttached || typeof document === "undefined") {
      return;
    }

    document.addEventListener("keydown", this.onKeyDown);
    this.isKeyDownAttached = true;
  }

  private detachKeyDown(): void {
    if (!this.isKeyDownAttached || typeof document === "undefined") {
      return;
    }

    document.removeEventListener("keydown", this.onKeyDown);
    this.isKeyDownAttached = false;
  }

  private attachDocumentMouseDown(): void {
    if (
      this.isDocumentMouseDownAttached ||
      typeof document === "undefined" ||
      !this.onDocumentMouseDown
    ) {
      return;
    }

    document.addEventListener("mousedown", this.onDocumentMouseDown);
    this.isDocumentMouseDownAttached = true;
  }

  private detachDocumentMouseDown(): void {
    if (
      !this.isDocumentMouseDownAttached ||
      typeof document === "undefined" ||
      !this.onDocumentMouseDown
    ) {
      return;
    }

    document.removeEventListener("mousedown", this.onDocumentMouseDown);
    this.isDocumentMouseDownAttached = false;
  }

  private lockScroll(): void {
    if (this.isScrollLocked) {
      return;
    }

    lockBodyScroll();
    this.isScrollLocked = true;
  }

  private unlockScroll(): void {
    if (!this.isScrollLocked) {
      return;
    }

    unlockBodyScroll();
    this.isScrollLocked = false;
  }
}

export function matchesViewportQuery(query: string): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia(query).matches
  );
}
